// ---------------------------------------------------------------------------
// queryClient — the single TanStack Query client for the webapp.
//
// This is the app-wide store for WorkflowSvc/Datalake server state: caching,
// request deduplication, background refetch, polling, and mutation-driven
// invalidation all flow through it. Created once at startup (see main.tsx) and
// provided via <QueryClientProvider>.
//
// Conventions for adding queries live in [[query/README]]. In short: bundle
// each resource's key + fetcher with the `queryOptions` helper, put every
// variable the fetcher reads into the query key, and inject the IHttpClient by
// closing over `useHttpClient()` inside the queryFn rather than importing a
// singleton.
// ---------------------------------------------------------------------------

import { QueryClient } from '@tanstack/react-query';

import { AppError } from '../api/errors';

// Retry only transient failures. A 4xx from WorkflowSvc (bad request, not
// found, forbidden) will never succeed on retry, so fail fast and surface the
// error; 5xx / network errors / timeouts are retried a couple of times with
// TanStack's exponential backoff.
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof AppError && error.statusCode !== undefined) {
    if (error.statusCode >= 400 && error.statusCode < 500) {
      // 408 Request Timeout and 429 Too Many Requests are worth retrying.
      return error.statusCode === 408 || error.statusCode === 429;
    }
  }
  return true;
}

/**
 * Build a fresh QueryClient with the webapp's shared defaults. Exported as a
 * factory (not a singleton) so tests can spin up an isolated client per test
 * with retries turned off — see `src/test/renderWithQueryClient`.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Server data is treated as fresh for 30s before a background refetch
        // is eligible — long enough to dedupe the burst of mounts on a page
        // load, short enough that navigating back shows current data.
        staleTime: 30_000,
        // Cache entries with no observers are dropped after 5 minutes.
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        // The app is not a dashboard that must live-update on focus; explicit
        // refetch/invalidate covers the cases that need freshness. Avoids a
        // refetch storm every time the user tabs back.
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
